/**
 * Enum of all feature gate keys used in oauth-consent.
 *
 * Each entry should include a comment linking to the Switcheroo gate URL.
 * Example:
 *   // https://switcheroo.atlassian.com/ui/gates/<gate-id>/key/<gate-key>
 *   MY_FEATURE = 'oauth-consent-my-feature',
 */
export enum OAuthConsentFeatureGate {
  // Temporary Nav4 rollout; remove this gate and the Nav3 shell after rollout.
  // https://switcheroo.atlassian.com/ui/gates/867752f5-4d8e-48a7-addc-1707eb8da0dc/key/oauth-consent_nav4-migration
  NAV4_MIGRATION = 'oauth-consent_nav4-migration',

  // Limits Rovo consent to one selected site; disabled preserves multi-site selection.
  // https://switcheroo.atlassian.com/ui/gates/26308305-524d-4fc6-8a96-90c232718d8f/key/oauth-consent_rovo-single-site
  ROVO_SINGLE_SITE = 'oauth-consent_rovo-single-site',

  // Deprecates most-used-sites activity fetching for Rovo and Kaizen when true.
  // https://switcheroo.atlassian.com/ui/gates/83be3652-4ac7-42cc-b958-5a94103c1ed7/key/oauth-consent_deprecate-most-used-sites
  DEPRECATE_MOST_USED_SITES = 'oauth-consent_deprecate-most-used-sites',

  // Gates Trello consent screen access to app authors/collaborators (Horsefly program).
  // https://switcheroo.atlassian.com/ui/gates/7d71dd9e-26b8-4587-b202-8ef2814277a1/key/oauth-consent_trello-contributor-gate
  TRELLO_CONTRIBUTOR_GATE = 'oauth-consent_trello-contributor-gate',

  // Internal bypass for site selector when /consent/info returns no workspaces (e.g. timeout).
  // NOT intended for production use — shown only to internal users via this gate.
  SITE_SELECTOR_BYPASS = 'oauth-consent_site-selector-bypass',

  // Sorts the workspace list in MCP-style site selectors.
  MCP_SORT_WORKSPACES = 'oauth-consent_mcp-sorted-workspaces',

  // Groups the MCP site picker's options under non-selectable organization
  // headings read from `availableOrganizations`. Responses without named orgs
  // keep the flat list.
  MCP_GROUP_SITES_BY_ORG = 'oauth-consent_mcp-group-sites-by-org',

  // Enables the Trello workspace selector alongside the Atlassian site selector
  // in mixed-scope MCP consent flows. When disabled, the Trello workspace
  // selector is hidden even if hasTrelloProductAccess is true and Trello scopes
  // are present.
  TRELLO_MCP_MIXED_WORKSPACE = 'oauth-consent_trello-mcp-mixed-workspace',

  // Lets the MCP site and Bitbucket workspace selectors each hold more than one
  // selection (up to a fixed cap) in the site / site+Bitbucket / Bitbucket
  // flows. Forge, Agent and Trello flows stay single-select.
  MCP_MULTI_SITE_BITBUCKET_SELECTION = 'oauth-consent_mcp-multi-site-bitbucket-selection',

  // Splits the MCP consent into two steps (select sites/workspaces, then review
  // permissions) with Next/Back in the footer. Only takes effect where the
  // site/Bitbucket multi-select is also on.
  MCP_TWO_STEP_CONSENT = 'oauth-consent_mcp-two-step-consent',
}
